import { Globals } from 'csstype'
import React, { forwardRef } from 'react'

import { lightMidGray } from '@/_vars'
import { Button } from '@/atoms/Button'
import { SvgButton } from '@/atoms/SvgButton'
import { CloseIcon, closeIconStyles } from '@/atoms/SvgIcon'
import { passResponsive, ResponsiveProps } from '@/css-helpers/media-query'
import { forceArray } from '@/lib/array-utils'
import { E2E } from '@/lib/e2e-utils'
import CrossRoundIcon from '@/refactor/assets/icons/cross-round.svg'
import { backgroundGray, brightRed, disabledGray, linkBlue, mainBlack } from '@/refactor/colors'
import { font, FontFamily, FontProps, FontType, pixelFontSize } from '@/refactor/fonts'
import styled, { css, flex, FlexProps, grid, GridProps, mediaQuery, muiUtils, padMulti, padPx, size } from '@/styles'
import { ArrayOr } from '@/types/common'

export type { FlexProps, GridProps } from '@/styles'

// eslint-disable-next-line no-restricted-syntax
export enum PageMode {
	Edit = 'edit',
	Create = 'create',
	View = 'view',
}

export const PageTitle = styled.h1<{ flex?: FlexProps | boolean; bottomMargin?: number | string }>`
	${({ flex: $flex, bottomMargin: $bottomMargin }) => css`
		color: ${mainBlack};
		${() => font(FontType.Comfortaa, { size: 32 })};
		${() => ($flex ? flex({ align: 'center', gap: 10, ...(typeof $flex === 'object' ? $flex : {}) }) : '')};
		${() => ($bottomMargin ? `margin-bottom: ${padPx($bottomMargin)};` : '')};

		${muiUtils.mobile} {
			font-family: ${FontFamily.Comfortaa};
			font-size: ${(p) => p.theme.typography.pxToRem(20)};
			line-height: ${(p) => p.theme.typography.pxToRem(28)};
			font-weight: bold;
		}
	`}
`

export const PageSubtitle = styled.div`
	color: ${lightMidGray};
	font-size: ${pixelFontSize(16)};
	line-height: 1.4;
`

export const AddButton = styled(Button).attrs({ color: 'primary' })`
	min-width: 160px;

	${muiUtils.mobile} {
		padding: ${(p) => p.theme.spacing(1)};
	}

	${muiUtils.desktop} {
		margin-left: ${(p) => p.theme.spacing(3)};
	}
`

export const CloseButton = styled(CloseIcon)`
	cursor: pointer;
	transition: color 150ms ease-in-out;

	&:hover {
		color: ${linkBlue};
	}
`

export const RoundCloseButton = styled(SvgButton).attrs({ src: CrossRoundIcon })`
	${closeIconStyles()}
`

interface PositionedProps extends E2E {
	className?: string
	style?: React.CSSProperties
	zIndex: React.CSSProperties['zIndex']
	position: React.CSSProperties['position']
	display: React.CSSProperties['display']
	top: React.CSSProperties['top']
	right: React.CSSProperties['right']
	bottom: React.CSSProperties['bottom']
	left: React.CSSProperties['left']
	onKeyDown?: React.KeyboardEventHandler<HTMLElement>
	onClick?: React.MouseEventHandler
}

// TODO: BCDTDRCT-1844 rework this component on styled.div, check that nothing is broken
const Positioned = forwardRef<HTMLDivElement, React.PropsWithChildren<Partial<PositionedProps>>>((props, ref) => {
	const { className, children, style, position, zIndex, display, top, right, bottom, left, e2e, ...rest } = props

	return (
		<div
			ref={ref}
			className={className}
			data-test={e2e}
			style={{ position, zIndex, display, top, right, bottom, left, ...style }}
			{...rest}
		>
			{children}
		</div>
	)
})

export const RelativeContainer = styled(Positioned).attrs({
	position: 'relative',
})``

export const AbsoluteContainer = styled(Positioned).attrs({
	position: 'absolute',
})``

export const StickyContainer = styled(Positioned).attrs({ position: 'sticky' })``

interface MediaQueryElProps {
	inline?: boolean
	width?: string
}

/** This element and its children will be only visible on desktop. */
export const DesktopOnly = styled.div<MediaQueryElProps>`
	${({ inline: $inline, width: $width }) => css`
		${mediaQuery.hideBelow('desktop')}
		${$inline ? `display: inline-block;` : ''}
		${$width ? `width: ` + $width : ''}
	`}
`

export const largeScreenOnlyCss = () => css`
	${muiUtils.mobile} {
		display: none;
	}
`

export const LargeScreenOnly = styled.div`
	${largeScreenOnlyCss()}
`

export const mobileOnlyCss = () => css`
	${muiUtils.desktop} {
		display: none;
	}
`

export const BelowLargeScreen = styled.div`
	${mobileOnlyCss()};
`

interface SizedBoxProps {
	as?: keyof React.JSX.IntrinsicElements
	width?: number | string
	height?: number | string
	maxWidth?: number | string
	maxHeight?: number | string
	inline?: boolean
	below?: {
		desktop?: {
			height?: number | string
		}
	}
}

export const SizedBox = styled.div<SizedBoxProps>`
	flex-shrink: 0;
	${() =>
		passResponsive(
			({ inline: $inline, width: $width, height: $height, maxWidth: $maxWidth, maxHeight: $maxHeight, as }) => `
		${as || $inline ? 'display: inline-block;' : ''}
		${$width !== undefined || $height !== undefined ? size($width ?? 'auto', $height ?? 'auto') : ''}
		${$maxWidth !== undefined ? `max-width: ${padPx($maxWidth)};` : ''}
		${$maxHeight !== undefined ? `max-height: ${padPx($maxHeight)};` : ''}
	`,
		)}
`
export const Font = styled.span<
	Partial<FontProps> & { mobileProps?: Partial<FontProps> } & {
		textWrap?: 'balance' | 'pretty' | 'stable' | 'wrap' | 'nowrap'
	}
>`
	${({ color: $color, mobileProps: $mobileProps, textWrap: $textWrap, ...props }) => css`
		color: ${$color};
		${font(props)}
		${$mobileProps ? mediaQuery.mobileOnly`${font(props)}` : ''}
		${$textWrap ? `text-wrap: ${$textWrap};` : ''}
	`}
`

export const NoWrap = styled.span<{ display?: string }>`
	white-space: nowrap;
	${({ display: $display }) => ($display ? `display: ${$display};` : '')}
`
export const TextAlign = styled.div<{ align?: React.CSSProperties['textAlign'] }>`
	text-align: ${({ align: $align }) => $align};
`
export const Center = styled.div`
	${flex({ justify: 'center', align: 'center', direction: 'column' })}
`

export const FillAbsolute = styled.div`
	position: absolute;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
`

export const FillFixed = styled(FillAbsolute)`
	position: fixed;
`

export const centeredAbsoluteCss = () => css`
	position: absolute;
	left: 50%;
	top: 50%;
	transform: translate(-50%, -50%);
`

export const CenteredAbsolute = styled(Center)`
	${centeredAbsoluteCss()};
`
export const Expanded = styled.div`
	flex: 1;
`

/** @deprecated use Stack instead */
export const Flex = styled.div<FlexProps & { mobileProps?: FlexProps }>`
	${(props) =>
		flex({
			justify: 'center',
			align: 'center',
			direction: 'row',
			...props,
		})};
	${muiUtils.mobile} {
		${({ mobileProps: $mobileProps }) => ($mobileProps ? flex($mobileProps) : '')}
	}
`

interface FlexItemProps {
	grow?: number
	shrink?: number
	width?: number | string
	maxWidth?: number
	minWidth?: number
	allignSelf?: React.CSSProperties['alignSelf']
}
export const FlexItem = styled.div<FlexItemProps>`
	${({
		grow: $grow,
		shrink: $shrink,
		width: $width,
		minWidth: $minWidth,
		maxWidth: $maxWidth,
		allignSelf: $allignSelf,
	}) => css`
		${`flex-grow: ${$grow ?? 0};`}
		${`flex-shrink: ${$shrink ?? 1};`}
	${`flex-basis: ${padPx($width) ?? 'auto'};`}
	${$minWidth ? `min-width: ${padPx($minWidth)};` : ''}
	${$maxWidth ? `max-width: ${padPx($maxWidth)};` : ''}
	${$allignSelf ? `allign-self: ${$allignSelf};` : ''}
	`}
`
export const Grid = styled.div<GridProps & { mobileProps?: GridProps }>`
	${(props) => grid(props)};

	${muiUtils.mobile} {
		${({ mobileProps: $mobileProps }) => ($mobileProps ? grid($mobileProps) : undefined)}
	}
`

export const GridItem = styled.div<{
	column?: number | string
	row?: number | string
	mobileColumn?: number | string
	mobileRow?: number | string
	flex?: string | number
	justify?: 'start' | 'end' | 'center' | 'stretch'
	align?: 'start' | 'end' | 'center' | 'stretch'
	color?: string
}>`
	${({
		column: $column,
		row: $row,
		mobileColumn: $mobileColumn,
		mobileRow: $mobileRow,
		flex: $flex,
		justify: $justify,
		align: $align,
		color: $color,
	}) => css`
		${$column ? `grid-column: ${$column};` : ''}
		${$row ? `grid-row: ${$row};` : ''}
	${$mobileColumn ? mediaQuery.mobileOnly`grid-column: ${$mobileColumn};`.join('') : ''}
	${$mobileRow ? mediaQuery.mobileOnly`grid-row: ${$mobileRow};`.join('') : ''}
	${$flex ? `flex: ${$flex};` : ''}
	${$justify
			? css`
					justify-self: ${$justify};
				`
			: ''}
	${$align
			? css`
					align-self: ${$align};
				`
			: ''}
	${$color ? `color: ${$color};` : ''}
	`}
`

export const VerticalScroll = styled.div<{ maxHeight: number | string }>`
	max-height: ${({ maxHeight: $maxHeight }) => padPx($maxHeight)};
	overflow-y: auto;
`

export const HorizontalScroll = styled.div<{ maxWidth: number | string; height: number | string }>`
	${({ maxWidth: $maxWidth, height: $height }) => css`
		max-width: ${padPx($maxWidth)};
		overflow-x: auto;
		${$height ? `height:${padPx($height)}` : ''};
	`}
`

export const Bold = styled.b<{ fontWeight?: number | string }>`
	font-weight: ${({ fontWeight: $fontWeight, theme }) => $fontWeight || theme.typography.fontWeightBold};
`

export const Link = styled.a<ResponsiveProps<{ disabled?: boolean; color?: string; font?: Partial<FontProps> }>>`
	${passResponsive(
		({ color: $color, font: $font, disabled: $disabled, onClick: $onClick, href: $href }) => `
		${$font ? font($font, { useDefaultBase: false }) : ''}
		color: ${!$disabled ? ($color ?? linkBlue) : disabledGray};
		cursor: ${!$disabled && ($onClick || $href) ? 'pointer' : 'default'};
		text-decoration: none;

		&:hover {
			${!$disabled && ($onClick || $href) ? `text-decoration: underline;` : ''}
		}
	`,
	)}
`

export const LinkLike = styled(Link).attrs(() => ({ as: 'span' }))``

export const Hr = styled.hr<{
	color?: string
	gutterSize?: Array<number | string | undefined> | number | string
	fullWidth?: boolean
}>`
	${({ color: $color, gutterSize: $gutterSize, fullWidth: $fullWidth }) => css`
		color: ${$color ?? backgroundGray};
		background: currentColor;
		height: 1px;
		flex-basis: 1px;
		flex-shrink: 0;
		border: none;
		${$gutterSize !== undefined ? gutterSize($gutterSize) : ''}
		${$fullWidth ? 'width: 100%;' : ''}
	`}
`

function gutterSize(sz: ArrayOr<number | string | undefined>) {
	const _sz = forceArray(sz, false)
	const def = 30

	return `
		margin-top: ${padPx(_sz[0] ?? def)};
		margin-bottom: ${padPx(_sz[_sz.length - 1] ?? def)};
	`
}

/** @deprecated MUI controls add their own asterisk */
export const Asterisk = styled.span`
	color: ${brightRed};
`
type RepeatStyle = 'no-repeat' | 'repeat' | 'repeat-x' | 'repeat-y' | 'round' | 'space'

export interface BgImageProps {
	src: string
	width?: number | string
	height?: number | string
	size?: Globals | React.CSSProperties['backgroundSize']
	position?: Globals | React.CSSProperties['backgroundPosition']
	repeat?: Globals | RepeatStyle
	style?: React.CSSProperties
}

export const BgImage = styled.div.attrs<Pick<BgImageProps, 'src' | 'style'>>(({ src, style }) => ({
	style: { backgroundImage: `url(${src})`, ...style },
}))<BgImageProps>`
	${({ width: $width, height: $height, size: $size, position: $position, repeat: $repeat }) => css`
		${$width || $height ? `${size($width! ?? $height, $height)}` : ''}
		background-size: ${$size ?? 'contain'};
		background-position: ${$position ?? 'center'};
		background-repeat: ${$repeat ?? 'no-repeat'};
	`}
`

export const FirstChildWithMargin = styled.div<{ margin: number | Array<number> }>`
	> *:first-child {
		margin: ${({ margin: $margin }) => padMulti($margin)};
	}
`

export const Line = styled.div`
	display: flex;
	gap: 15px;
	${mediaQuery.smallMobile`
		gap: 10px;
	`}
`
